'use client';

import type { ClipBlock } from '@/lib/reel-studio/agent/blocks';

import { ClipGrid } from './ClipPlayer';
import { OpenInReelStudio } from './OpenInReelStudio';

/** Videos were collected for the question but none has a transcript, so nothing was searched. */
function nothingTranscribed(block: ClipBlock): boolean {
    return (
        block.kind === 'search' && block.transcribedCount === 0 && (block.collectedCount ?? 0) > 0
    );
}

/** The coverage line under a search: how much of the question's video could be searched. */
function coverageNote(block: ClipBlock): string | null {
    if (block.kind === 'search' && block.transcriptsUnavailable) {
        return "This question's analysis has its transcripts, but they can't be read for a search right now. Reel Studio may show them.";
    }
    if (block.kind !== 'search' || block.collectedCount == null || block.transcribedCount == null) {
        return null;
    }
    const { collectedCount: collected, transcribedCount: transcribed } = block;
    if (collected > 0 && transcribed === 0) {
        return `None of the ${collected.toLocaleString()} videos for this question are transcribed yet, so there is nothing to search.`;
    }
    if (transcribed < collected) {
        const missing = collected - transcribed;
        return `${missing.toLocaleString()} of ${collected.toLocaleString()} videos are not transcribed yet, so they weren't searched.`;
    }
    return null;
}

function heading(block: ClipBlock): string {
    if (block.kind === 'search') {
        if (block.transcriptsUnavailable) return "Transcripts can't be searched right now";
        if (nothingTranscribed(block)) return 'Nothing to search yet';
        const count = block.matchCount ?? block.clips.length;
        const noun = count === 1 ? 'moment mentions' : 'moments mention';
        return count === 0
            ? `No moments mention “${block.query}”`
            : `${count.toLocaleString()} ${noun} “${block.query}”`;
    }
    if (block.kind === 'theme' && block.themeLabel) return `Clips: ${block.themeLabel}`;
    return 'Clips';
}

/** Playable clips from a search, a theme or a pick, with their quotes. */
export function ClipBlockView({ block }: { block: ClipBlock }) {
    const shownOfMore =
        block.kind === 'search' &&
        block.matchCount != null &&
        block.matchCount > block.clips.length &&
        block.clips.length > 0;

    return (
        <section className="my-3 flex flex-col gap-3 rounded-xl border border-default-200 bg-content2/40 p-4">
            <header className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                    <h4 className="text-sm font-semibold text-foreground">{heading(block)}</h4>
                    <p
                        className="line-clamp-2 text-xs text-default-500"
                        title={block.scope.questionText}
                    >
                        {block.scope.questionText || block.scope.projectName}
                        {shownOfMore ? ` · showing ${block.clips.length}` : ''}
                    </p>
                </div>
                <OpenInReelStudio
                    scope={block.scope}
                    tab={block.kind === 'search' ? 'transcripts' : 'synthesis'}
                />
            </header>
            {block.clips.length > 0 && <ClipGrid clips={block.clips} />}
            {coverageNote(block) && (
                <p className="text-xs text-default-500">{coverageNote(block)}</p>
            )}
            {block.kind === 'search' && (block.lockedCount ?? 0) > 0 && (
                <p className="text-xs text-default-500">
                    Some transcripts are locked until this project&apos;s transcripts are unlocked,
                    so they weren&apos;t searched.
                </p>
            )}
        </section>
    );
}
